import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "./SEO";

const INK = "#0A0F1C";
const TEAL = "#3D9B92";
const MUTED = "#5B6472";
const LINE = "#C9CFD8";

/* ---------- shared bits ---------- */

const Blank = ({ w = "38mm" }) => (
  <span
    className="inline-block align-bottom"
    style={{ width: w, borderBottom: `0.35mm solid ${LINE}`, height: "5mm" }}
  />
);

const ScoreBoxes = () => (
  <div className="flex gap-[1.5mm]">
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        key={n}
        className="flex items-center justify-center font-mono"
        style={{
          width: "6mm",
          height: "6mm",
          border: `0.35mm solid ${LINE}`,
          fontSize: "8pt",
          color: MUTED,
        }}
      >
        {n}
      </span>
    ))}
  </div>
);

const WriteLine = ({ label }) => (
  <div className="mt-[4mm]">
    <div
      className="font-mono uppercase"
      style={{ fontSize: "6.5pt", letterSpacing: "0.15em", color: TEAL }}
    >
      {label}
    </div>
    <div style={{ borderBottom: `0.35mm solid ${LINE}`, height: "7mm" }} />
  </div>
);

function PageShell({ title, subtitle, course, children, pageNo, breakAfter }) {
  return (
    <div
      className={`relative bg-white mx-auto overflow-hidden ${
        breakAfter ? "print:break-after-page" : ""
      }`}
      style={{ width: "210mm", height: "297mm", color: INK, padding: "13mm 14mm" }}
    >
      {/* header */}
      <div
        className="flex items-end justify-between pb-[3mm]"
        style={{ borderBottom: `0.6mm solid ${INK}` }}
      >
        <div>
          <div className="flex items-center gap-[2mm]">
            <span style={{ width: "3mm", height: "3mm", background: TEAL }} />
            <span className="font-display font-bold" style={{ fontSize: "14pt" }}>
              Sebyx
            </span>
            <span
              className="font-mono uppercase"
              style={{ fontSize: "7pt", letterSpacing: "0.2em", color: MUTED }}
            >
              · {course} · Scorecard {pageNo}/3
            </span>
          </div>
          <div className="font-display font-bold mt-[2.5mm]" style={{ fontSize: "20pt" }}>
            {title}
          </div>
          <div style={{ fontSize: "9pt", color: MUTED, marginTop: "1mm" }}>{subtitle}</div>
        </div>
        <div className="text-right" style={{ fontSize: "8.5pt", color: MUTED }}>
          <div>
            Name <Blank w="34mm" />
          </div>
          <div className="mt-[2.5mm]">
            Week of <Blank w="34mm" />
          </div>
        </div>
      </div>

      {children}

      <div
        className="absolute bottom-[8mm] left-[14mm] right-[14mm] flex justify-between font-mono uppercase"
        style={{ fontSize: "6.5pt", letterSpacing: "0.15em", color: MUTED }}
      >
        <span>SEBYX ACADEMY · {course}</span>
        <span>sebyx.com · +91 91084 36667</span>
      </div>
    </div>
  );
}

/* ---------- Page 1: Presence self-check ---------- */

function PresencePage({ cfg }) {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  return (
    <PageShell
      title="The 5-Second Signals — Daily Self-Check"
      subtitle="Score yourself 1–5 on every signal, every working day — before your first customer, not after your last."
      course={cfg.tag}
      pageNo={1}
      breakAfter
    >
      <table className="w-full mt-[7mm]" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th
              className="text-left font-mono uppercase pb-[2mm]"
              style={{ fontSize: "7pt", letterSpacing: "0.15em", color: TEAL }}
            >
              Signal
            </th>
            {days.map((d, i) => (
              <th
                key={i}
                className="font-mono pb-[2mm]"
                style={{ fontSize: "8pt", color: MUTED, width: "11mm" }}
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cfg.signals.map(([name, desc], i) => (
            <tr key={i} style={{ borderTop: `0.35mm solid ${LINE}` }}>
              <td className="py-[3.5mm] pr-[4mm]">
                <div className="font-bold" style={{ fontSize: "10.5pt" }}>
                  {name}
                </div>
                <div style={{ fontSize: "8pt", color: MUTED }}>{desc}</div>
              </td>
              {days.map((_, j) => (
                <td key={j} className="text-center">
                  <span
                    className="inline-block"
                    style={{ width: "8mm", height: "8mm", border: `0.35mm solid ${LINE}` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div
        className="mt-[6mm] p-[4mm]"
        style={{ background: "#F2F5F7", border: `0.35mm solid ${LINE}` }}
      >
        <div
          className="font-mono uppercase mb-[2mm]"
          style={{ fontSize: "6.5pt", letterSpacing: "0.15em", color: TEAL }}
        >
          Scoring key
        </div>
        <div className="grid grid-cols-3 gap-[4mm]" style={{ fontSize: "8.5pt" }}>
          <div>
            <b>1–2 · Off-duty.</b>{" "}
            <span style={{ color: MUTED }}>You'd walk past yourself. Fix it before the floor.</span>
          </div>
          <div>
            <b>3 · Acceptable.</b>{" "}
            <span style={{ color: MUTED }}>Nobody complains — nobody remembers either.</span>
          </div>
          <div>
            <b>4–5 · Premium.</b>{" "}
            <span style={{ color: MUTED }}>{cfg.premiumKey}</span>
          </div>
        </div>
      </div>

      <WriteLine label="My weakest signal this week" />
      <WriteLine label="The one fix I will make on Monday" />
    </PageShell>
  );
}

/* ---------- Page 2: Role-play scorecard (2 per page) ---------- */

function RolePlayCard({ cfg }) {
  return (
    <div style={{ height: "116mm" }} className="pt-[3mm]">
      <div className="flex justify-between items-center">
        <div
          className="font-mono uppercase"
          style={{ fontSize: "7pt", letterSpacing: "0.2em", color: TEAL }}
        >
          ◆ Role-Play Scorecard — scored by your observer
        </div>
        <div style={{ fontSize: "8pt", color: MUTED }}>
          Seller <Blank w="24mm" /> &nbsp; Observer <Blank w="24mm" />
        </div>
      </div>

      <table className="w-full mt-[2mm]" style={{ borderCollapse: "collapse" }}>
        <tbody>
          {cfg.rolePlay.map(([name, hint], i) => (
            <tr key={i} style={{ borderTop: `0.35mm solid ${LINE}` }}>
              <td className="py-[1.8mm] pr-[3mm]" style={{ width: "115mm" }}>
                <span className="font-bold" style={{ fontSize: "9pt" }}>
                  {i + 1}. {name}
                </span>{" "}
                <span style={{ fontSize: "7.5pt", color: MUTED }}>— {hint}</span>
              </td>
              <td>
                <ScoreBoxes />
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: `0.5mm solid ${INK}` }}>
            <td className="py-[1.5mm] font-bold" style={{ fontSize: "9.5pt" }}>
              Total
            </td>
            <td>
              <span
                className="inline-flex items-end justify-center font-bold"
                style={{
                  width: "16mm",
                  height: "7mm",
                  border: `0.5mm solid ${INK}`,
                  fontSize: "9pt",
                }}
              >
                &nbsp;/ 25
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-[6mm]">
        <div className="mt-[2mm]">
          <div
            className="font-mono uppercase"
            style={{ fontSize: "6.5pt", letterSpacing: "0.15em", color: TEAL }}
          >
            Best moment — steal it, it's yours now
          </div>
          <div style={{ borderBottom: `0.35mm solid ${LINE}`, height: "6mm" }} />
        </div>
        <div className="mt-[2mm]">
          <div
            className="font-mono uppercase"
            style={{ fontSize: "6.5pt", letterSpacing: "0.15em", color: TEAL }}
          >
            One fix for the next round
          </div>
          <div style={{ borderBottom: `0.35mm solid ${LINE}`, height: "6mm" }} />
        </div>
      </div>
    </div>
  );
}

function RolePlayPage({ cfg }) {
  return (
    <PageShell
      title="Role-Play Scorecard"
      subtitle="One card per round. 22+ is drill-passed. Below 15 — replay the round, don't debate it. Cut along the line for two cards."
      course={cfg.tag}
      pageNo={2}
      breakAfter
    >
      <RolePlayCard cfg={cfg} />
      <div
        className="my-[1mm]"
        style={{ borderTop: `0.35mm dashed ${MUTED}`, position: "relative" }}
      >
        <span
          className="font-mono absolute"
          style={{ fontSize: "6pt", color: MUTED, top: "-2mm", right: 0, background: "#fff" }}
        >
          ✂ CUT
        </span>
      </div>
      <RolePlayCard cfg={cfg} />
    </PageShell>
  );
}

/* ---------- Page 3: Weekly performance ---------- */

function WeeklyPage({ cfg }) {
  return (
    <PageShell
      title="Weekly Performance Scorecard"
      subtitle="Every Sunday, 10 minutes, brutal honesty. Score the 8 frameworks, write the numbers, post one win in the alumni group."
      course={cfg.tag}
      pageNo={3}
    >
      <div
        className="font-mono uppercase mt-[6mm] mb-[2mm]"
        style={{ fontSize: "7pt", letterSpacing: "0.2em", color: TEAL }}
      >
        ◆ The 8 frameworks — how sharp was each this week?
      </div>
      <table className="w-full" style={{ borderCollapse: "collapse" }}>
        <tbody>
          {cfg.frameworks.map(([mod, name], i) => (
            <tr key={i} style={{ borderTop: `0.35mm solid ${LINE}` }}>
              <td className="py-[2mm] font-mono" style={{ fontSize: "8pt", color: TEAL, width: "10mm" }}>
                {mod}
              </td>
              <td className="py-[2mm] font-bold" style={{ fontSize: "9.5pt" }}>
                {name}
              </td>
              <td style={{ width: "40mm" }}>
                <ScoreBoxes />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        className="font-mono uppercase mt-[5mm] mb-[2mm]"
        style={{ fontSize: "7pt", letterSpacing: "0.2em", color: TEAL }}
      >
        ◆ The numbers — activity you controlled
      </div>
      <div className="grid grid-cols-4 gap-[4mm]">
        {cfg.numbers.map((label, i) => (
          <div key={i} className="p-[3mm]" style={{ border: `0.35mm solid ${LINE}` }}>
            <div style={{ fontSize: "7.5pt", color: MUTED, minHeight: "8mm" }}>{label}</div>
            <div style={{ borderBottom: `0.35mm solid ${LINE}`, height: "8mm" }} />
          </div>
        ))}
      </div>

      <WriteLine label="Win of the week — what worked? (post this in the alumni group)" />
      <WriteLine label="Lesson of the week — what did a customer teach you?" />
      <WriteLine label="Next week's one commitment — say it out loud" />
    </PageShell>
  );
}

/* ---------- main ---------- */

function Scorecards({ cfg }) {
  return (
    <main
      data-testid={`scorecards-${cfg.slug}`}
      className="min-h-screen font-body"
      style={{ background: "#3a4150" }}
    >
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group text-sebyx-text">
            <ArrowLeft size={16} className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform" />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">Sebyx</span>
          </Link>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
            {cfg.tag} · Participant Scorecards
          </span>
        </div>
      </header>
      <div className="py-6 print:py-0 space-y-6 print:space-y-0">
        <PresencePage cfg={cfg} />
        <RolePlayPage cfg={cfg} />
        <WeeklyPage cfg={cfg} />
      </div>
    </main>
  );
}

/* ---------- course configs ---------- */

const FRONTLINE_CFG = {
  slug: "frontline-elite",
  tag: "FRONTLINE ELITE",
  signals: [
    ["Grooming & Dress-Code", "Pressed, polished, on-code for premium. You belong next to the product."],
    ["Posture & Position", "Open stance, visible, ready. Never leaning, never buried in a phone."],
    ["Voice", "Slightly slower than casual, warm not loud. Premium never rushes."],
    ["Energy", "Customer #40 at 8 PM gets the same energy as customer #1."],
    ["Your Signature", "The one touch customers describe you by — worn and working today?"],
  ],
  premiumKey: "A customer would trust you with a ₹5-lakh purchase on sight.",
  rolePlay: [
    ["The Greeting", "3-step choreography: acknowledge, space with a promise, re-approach"],
    ["Discovery", "All 4 questions, in order, conversational — occasion, person, taste, range"],
    ["The Presentation", "Exactly 3 options, one recommendation, one reason in their words"],
    ["Objection Handling", "A.R.C. — acknowledged, reframed, confirmed. No discount reflex"],
    ["The Close", "An explicit, clean ask. Skipped close = max 1 point"],
  ],
  frameworks: [
    ["M1", "The 4 Identities — acted one level above my badge"],
    ["M2", "The 5-Second Signals — daily check done"],
    ["M3", "3-Step Greeting — zero “Can I help you?” slips"],
    ["M4", "6 Buying Reasons — read before I pitched"],
    ["M5", "4-Question Discovery — asked before showing"],
    ["M6", "A.R.C. — objections welcomed, not feared"],
    ["M7", "The 4 Clean Closes — asked, every time"],
    ["M8", "Little Black Book — captured & messaged same day"],
  ],
  numbers: [
    "Walk-ins I personally handled",
    "Explicit closes I attempted",
    "Customers added to my book (WhatsApp)",
    "Referrals I asked for",
  ],
};

const ROOKIE_CFG = {
  slug: "rookie-hunter",
  tag: "ROOKIE HUNTER",
  signals: [
    ["Grooming & Dress-Code", "Client-ready — in person and on camera. No 'just a call' excuses."],
    ["Camera & Call Presence", "Eye-level camera, light in front, pace 10% slower than chat."],
    ["Voice", "Steady, warm, unhurried — your voice is your suit on the phone."],
    ["Energy", "Dial #40 gets the same energy as dial #1. No zero days."],
    ["LinkedIn Presence", "Headline serves the buyer, one thoughtful comment posted today?"],
  ],
  premiumKey: "A CXO would take your call and remember your name.",
  rolePlay: [
    ["The Opener", "15 seconds: pattern interrupt, permission, point — earned the next 30?"],
    ["Discovery", "The 5 questions: situation, pain, impact, authority, next step"],
    ["Pain × Number", "Surfaced the pain AND attached a cost to it — no number, no score above 3"],
    ["Objection Handling", "A.R.C. — acknowledged, reframed, confirmed. No folding, no discount"],
    ["The Next Step", "Concrete step booked with a date. “I'll email you” = max 1 point"],
  ],
  frameworks: [
    ["M1", "Activity Math — hit my daily number, every day"],
    ["M2", "Digital Handshake — profile & presence working"],
    ["M3", "3×3 Opener — trigger, value, micro-ask on every touch"],
    ["M4", "Decision Unit Map — roles named on every live deal"],
    ["M5", "5-Question Discovery — no demo before diagnosis"],
    ["M6", "A.R.C. — big three answered without folding"],
    ["M7", "Thread & Close — multi-threaded, direct asks made"],
    ["M8", "7-Touch Cadence — every lead has a next-step date"],
  ],
  numbers: [
    "Dials / outreach touches made",
    "Real conversations held",
    "Meetings booked",
    "CRM records with a next-step date (%)",
  ],
};

export function FrontlineScorecards() {
  return (
    <>
      <SEO
        title="Frontline Elite Scorecards | Sebyx Academy"
        description="Participant self-evaluation scorecards for the Frontline Elite retail sales programme."
        path="/frontline-scorecards"
      />
      <Scorecards cfg={FRONTLINE_CFG} />
    </>
  );
}

export function RookieScorecards() {
  return (
    <>
      <SEO
        title="Rookie Hunter Scorecards | Sebyx Academy"
        description="Participant self-evaluation scorecards for the Rookie Hunter B2B sales programme."
        path="/rookie-scorecards"
      />
      <Scorecards cfg={ROOKIE_CFG} />
    </>
  );
}
